import { useConnection } from "@solana/wallet-adapter-react";
import { Connection } from "@solana/web3.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { withTimeout } from "../lib/async";
import { REFRESH_INTERVAL_MS, RPC_FALLBACK } from "../lib/constants";
import { loadMarkets } from "../lib/markets";
import { loadSolUsdPrice } from "../lib/oracle";
import type { Market } from "../lib/types";

interface MarketsState {
  markets: Market[];
  loading: boolean;
  refreshing: boolean;
  error?: string;
  updatedAt?: number;
  solUsdPrice?: number;
}

async function loadMarketData(connection: Connection) {
  const [markets, solUsdPrice] = await Promise.all([
    withTimeout(
      loadMarkets(connection),
      10_000,
      "The RPC timed out while loading Tribe markets",
    ),
    withTimeout(
      loadSolUsdPrice(connection),
      5_000,
      "The SOL/USD oracle request timed out",
    ).catch((error) => {
      console.warn("Unable to load the on-chain SOL/USD price", error);
      return undefined;
    }),
  ]);
  return { markets, solUsdPrice };
}

function readConnections(connection: Connection): Connection[] {
  const fallback =
    RPC_FALLBACK && RPC_FALLBACK !== connection.rpcEndpoint
      ? new Connection(RPC_FALLBACK, "confirmed")
      : undefined;
  // PublicNode restricts getMultipleAccounts to very small batches. Prefer the
  // large-batch read endpoint, then retain the wallet RPC as a fallback.
  return connection.rpcEndpoint.includes("publicnode.com") && fallback
    ? [fallback, connection]
    : [connection, ...(fallback ? [fallback] : [])];
}

export function useMarkets() {
  const { connection } = useConnection();
  const [state, setState] = useState<MarketsState>({
    markets: [],
    loading: true,
    refreshing: false,
  });
  const inFlight = useRef<Promise<void> | undefined>(undefined);

  const refresh = useCallback((): Promise<void> => {
    if (inFlight.current) return inFlight.current;
    const task = (async () => {
      setState((current) => ({
        ...current,
        loading: current.markets.length === 0,
        refreshing: current.markets.length > 0,
        error: undefined,
      }));
      try {
        let result: Awaited<ReturnType<typeof loadMarketData>> | undefined;
        let lastError: unknown;
        for (const readConnection of readConnections(connection)) {
          try {
            result = await loadMarketData(readConnection);
            break;
          } catch (error) {
            lastError = error;
            console.warn(`Unable to load markets from ${readConnection.rpcEndpoint}`, error);
          }
        }
        if (!result) throw lastError ?? new Error("Unable to refresh tokens");
        setState((current) => ({
          markets: result.markets,
          loading: false,
          refreshing: false,
          updatedAt: Date.now(),
          solUsdPrice: result.solUsdPrice ?? current.solUsdPrice,
        }));
      } catch (error) {
        console.error("Unable to load Tribe markets", error);
        setState((current) => ({
          ...current,
          loading: false,
          refreshing: false,
          error:
            error instanceof Error
              ? error.message
              : "Unable to refresh tokens",
        }));
      }
    })();
    inFlight.current = task;
    void task.finally(() => {
      if (inFlight.current === task) inFlight.current = undefined;
    });
    return task;
  }, [connection]);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, REFRESH_INTERVAL_MS);
    return () => {
      window.clearInterval(interval);
    };
  }, [refresh]);

  return { ...state, refresh };
}
